import { api } from "../api";

// ================================================================
// Types
// ================================================================

export interface CryptoOverviewNetwork {
  code: string;
  name: string;
  /**
   * Иконка сети из админки. Единый формат `renderCurrencyIcon`:
   *   • `iconify:solar:bitcoin-bold-duotone` — рендер через @iconify/react;
   *   • `https://…/logo.png` — URL картинки;
   *   • `data:image/…;base64,…` — inline base64 загруженный из админки.
   */
  icon: string | null;
  min_deposit: string | null;
  min_withdraw: string | null;
  explorer_url: string | null;
  dvnet_currency_id: string | null;
}

export interface CryptoOverviewItem {
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  icon: string | null;
  balance: string;
  networks: CryptoOverviewNetwork[];
}

export interface CryptoWallet {
  id: string;
  network_code: string;
  network_name: string;
  currency_code: string;
  address: string;
  memo: string | null;
  explorer_url: string | null;
  min_deposit: string | null;
  dvnet_wallet_id: string | null;
  created_at: string;
}

export interface CryptoTransaction {
  id: string;
  type: "DEPOSIT" | "WITHDRAW";
  status: "NEW" | "PENDING" | "IN_PROGRESS" | "SUCCEEDED" | "CANCELLED" | "FAILED";
  currency: string | null;
  amount: string | null;
  network_code: string | null;
  address: string | null;
  tx_hash: string | null;
  dvnet_tx_id: string | null;
  bc_uniq_key: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface WithdrawPayload {
  networkCode: string;
  currencyCode: string;
  addressTo: string;
  amount: string;
  memo?: string;
}

// ================================================================
// API calls
// ================================================================

/** Обзор: список крипто-валют + балансы + доступные сети. */
export async function getCryptoOverview(): Promise<CryptoOverviewItem[]> {
  const { data } = await api.get<CryptoOverviewItem[]>("/me/crypto");
  return data;
}

/** Все депозит-адреса юзера. */
export async function listCryptoWallets(): Promise<CryptoWallet[]> {
  const { data } = await api.get<CryptoWallet[]>("/me/crypto/wallets");
  return data;
}

export interface ProvisionResult {
  created: number;
  skipped: number;
  failed: number;
  errors: Array<{ network: string; currency: string; error: string }>;
}

/**
 * Догенерировать все депозит-адреса, которых у юзера ещё нет.
 * Идемпотентно: если адреса уже есть — вернёт `skipped`, если DV.net упал
 * по какой-то сети — вернёт `failed` + `errors` без исключения.
 *
 * Полезно как catch-up, если юзер прошёл онбординг ДО деплоя автосоздания.
 */
export async function provisionCryptoWallets(): Promise<ProvisionResult> {
  const { data } = await api.post<ProvisionResult>(
    "/me/crypto/wallets/provision",
    {},
  );
  return data;
}

/**
 * Получить или создать депозит-адрес для конкретной сети.
 * DV.net сгенерирует уникальный адрес, привязанный к этому userId.
 */
export async function getOrCreateCryptoWallet(
  networkCode: string,
  currencyCode: string,
): Promise<CryptoWallet> {
  const { data } = await api.post<CryptoWallet>("/me/crypto/wallets", {
    networkCode,
    currencyCode,
  });
  return data;
}

/** Инициировать вывод крипты. */
export async function submitCryptoWithdraw(payload: WithdrawPayload): Promise<CryptoTransaction> {
  const { data } = await api.post<CryptoTransaction>("/me/crypto/withdraw", payload);
  return data;
}

/** История крипто-транзакций. */
export async function listCryptoTransactions(limit = 50): Promise<CryptoTransaction[]> {
  const { data } = await api.get<CryptoTransaction[]>("/me/crypto/transactions", {
    params: { limit },
  });
  return data;
}
